import { Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Rocket, Users, Code, Heart, Target, Zap } from 'lucide-react';

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      <Navbar />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero */}
        <div className="text-center mb-16">
          <div className="w-16 h-16 bg-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Rocket className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-slate-900 mb-4">About PDX Hacks</h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Portland's premier hackathon community, bringing together developers, designers, 
            and innovators to build amazing projects.
          </p>
        </div>

        {/* Mission */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
            <Target className="w-6 h-6 text-indigo-600" />
            Our Mission
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            PDX Hacks exists to foster a thriving tech community in Portland. We believe that 
            collaboration, creativity, and continuous learning are the keys to innovation. 
            Our platform provides a space for hackers of all skill levels to showcase their 
            projects, connect with mentors, and find their next collaborators.
          </p>
        </section>

        {/* Values */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
            <Heart className="w-6 h-6 text-indigo-600" />
            Our Values
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { title: 'Inclusivity', desc: 'Everyone is welcome, regardless of background or experience level.' },
              { title: 'Collaboration', desc: 'The best projects come from working together and sharing knowledge.' },
              { title: 'Learning', desc: 'Every hackathon is an opportunity to grow and discover new technologies.' },
              { title: 'Community', desc: 'We support each other and celebrate every achievement, big or small.' },
            ].map((value) => (
              <Card key={value.title}>
                <CardContent className="p-5">
                  <h3 className="font-semibold text-slate-900 mb-2">{value.title}</h3>
                  <p className="text-slate-600">{value.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* What We Do */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
            <Zap className="w-6 h-6 text-indigo-600" />
            What We Do
          </h2>
          <div className="space-y-4 text-slate-600">
            <p>
              <strong className="text-slate-900">Hackathons:</strong> We organize regular hackathon 
              events ranging from 24-hour sprints to week-long challenges, covering various themes 
              from social good to cutting-edge tech.
            </p>
            <p>
              <strong className="text-slate-900">Workshops:</strong> Learn new skills through hands-on 
              workshops led by industry professionals and experienced community members.
            </p>
            <p>
              <strong className="text-slate-900">Mentorship:</strong> Connect with experienced developers 
              who can guide your projects and career.
            </p>
            <p>
              <strong className="text-slate-900">Community:</strong> Join our forums to discuss ideas, 
              get feedback, find team members, and share your wins.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center bg-slate-50 rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Ready to Join?</h2>
          <p className="text-slate-600 mb-6">
            Become part of Portland's most active hackathon community today.
          </p>
          <div className="flex justify-center gap-4">
            <Link to="/register">
              <Button className="bg-indigo-600 hover:bg-indigo-700">
                Get Started
              </Button>
            </Link>
            <Link to="/events">
              <Button variant="outline">
                View Events
              </Button>
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
}
